/* eslint-disable max-len */
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../../modules/auth/useAuth";
import styled from "styled-components";
import {
  Breadcrumb,
  Button,
  Dropdown,
  DropdownButton,
  Modal,
  Spinner,
  Toggle,
} from "@xfrontend/xui-react-plus";
import { Edit } from "@xfrontend/xicon";
import { useTranslation } from "react-i18next";
import ReactScrollbarsCustom from "#/modules/common/ReactScrollbarsCustom";
import { toast } from "#/infra/Toastify";
import PolicySelectorModal, { PolicyOption } from "#/modules/common/PolicySelectorModal";
import RiskLevelTag from "#/modules/common/RiskLevelTag";
import StatusTag from "#/modules/common/StatusTag";
import ViolationPolicyTypeTag from "#/modules/common/ViolationPolicyTypeTag";
import PolicyCategoryTag from "#/modules/common/PolicyCategoryTag";
import { RootState } from "#/types/RootState";
import { DashboardItem } from "#/apis/infra/getDashboard";
import {
  POLICY_DISPLAY_NAMES, StatisticsResponse, getScoreColor, sortByRiskLevelDesc,
} from "#/types/xecguard";
import { getPolicies } from "#/modules/policies/slice";
import { fetchServiceTokens, fetchStatistics } from "#/infra/redux/actionCreator";
import { formatTimestamp } from "#/utils/analytics";
import {
  getPolicyNameMapping, getDashboardData, getLicense,
} from "#/modules/dashboard/redux/actionCreator";
import ThreatTraceSection from "#/modules/dashboard/components/ThreatTraceSection";
import { FETCHED_SERVICE_TOKENS } from "#/infra/redux/actionType";
import {
  ServiceTokenType,
  patchServiceToken,
  deleteServiceToken,
  getServiceToken,
  regenerateServiceToken,
} from "#/apis/infra/getServiceTokens";
import PanelProvider from "#/modules/common/PanelProvider";
import { useLanguage } from "#/i18n/LanguageContext";
import { getPolicyLocale } from "#/i18n/policyTranslations";

// Removed DetailContainer, MainContent, PanelWrapper

const DetailPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  overflow: hidden;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  flex: 1;
  height: 100%;
  min-height: 0;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  overflow: hidden;
  min-height: 0;

  @media (min-width: 768px) {
    width: 339px;
    flex-shrink: 0;
  }
`;

const LeftPanelInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0;
`;

const RightPanel = styled.div`
  overflow: hidden;
  min-height: 0;
`;

const RightPanelInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled.div`
  border-radius: 4px;
  margin-bottom: 6px;
`;

const ServiceDetailCard = styled.div`
  border-top: 1px solid var(--x-purple-400, #6a6280);
  border-bottom: 1px solid var(--x-purple-400, #6a6280);
  padding-top: 18px;
  padding-bottom: 18px;
`;

const AppTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: var(--x-gray-200);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AppDescription = styled.p`
  font-size: 14px;
  color: var(--x-gray-300);
  margin: 0;
  line-height: 1.6;
`;

const StatsDetailCard = styled.div`
  width: 100%;
  padding: 12px;
  background: var(--x-purple-600, #3a3646);
  border-radius: 4px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 0;
  display: inline-flex;
`;

const StatsDetailRow = styled.div`
  align-self: stretch;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  display: inline-flex;
`;

const StatsDetailLabel = styled.div`
  width: 131px;
  color: var(--x-gray-500, #a9a9ac);
  font-size: 14px;
  font-family: Inter;
  font-weight: 400;
  line-height: 21px;
  word-wrap: break-word;
`;

const StatsDetailValue = styled.div<{ monospace?: boolean }>`
  text-align: right;
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  font-family: ${(props: { monospace?: boolean }) => (props.monospace ? "Roboto Mono" : "Inter")};
  font-weight: 400;
  line-height: 21px;
  word-wrap: break-word;
`;

const StatsDetailDivider = styled.div`
  align-self: stretch;
  height: 3px;
  position: relative;
`;

const SectionTitle = styled.h3`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  color: var(--x-gray-400, #cfd0d3);
  margin: 0 0 12px 0;
  word-wrap: break-word;
`;

const PoliciesSummaryRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const PoliciesSummaryText = styled.div`
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  line-height: 21px;
  flex: 1;
`;

const TokenDetailItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 3px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  color: var(--x-gray-500, #a9a9ac);
  font-size: 14px;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  line-height: 21px;
  word-wrap: break-word;
`;

const DetailValue = styled.span<{ monospace?: boolean }>`
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  font-family: ${(props: { monospace?: boolean }) => (props.monospace ? "\"Roboto Mono\", monospace" : "Inter")};
  font-weight: 400;
  line-height: 21px;
  word-wrap: break-word;
`;

const TabContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 24px;
`;

const Tab = styled.button<{ active: boolean }>`
  height: 32px;
  line-height: 14px;
  color: ${(props: { active: boolean }) => (props.active ? "var(--x-gray-200)" : "var(--x-gray-400)")};
  background-color: ${(props: { active: boolean }) => (props.active ? "var(--x-purple-400)" : "transparent")};
  border: 1px solid var(--x-purple-500);
  border-radius: 4px;
  padding: 8px 11px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  transition: all 0.2s;

  &:hover {
    color: var(--x-gray-200);
    background-color: ${(props: { active: boolean }) => (props.active ? "var(--x-purple-400)" : "var(--x-purple-500)")};
  }

  &:disabled {
    color: var(--x-gray-600);
    background-color: transparent;
    border: unset;
    cursor: not-allowed;
  }
`;

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const TabBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  background: var(--x-gray-200, #f6f6f7);
  color: var(--x-gray-800, #41454c);
  font-size: 14px;
  line-height: 1;
  padding: 0 6px;
`;

const PolicyCard = styled.div`
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  margin-bottom: 12px;
`;

const PolicyCardName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--x-gray-200, #f6f6f7);
`;

const PolicyCardDescription = styled.p`
  font-size: 13px;
  color: var(--x-gray-300);
  margin: 8px 0 12px;
  line-height: 1.5;
`;

const PolicyCardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PolicyFooterLabel = styled.span`
  font-size: 13px;
  color: var(--x-gray-500, #a9a9ac);
`;

const ViolationsCount = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--x-red, #ff595a);
`;

const SafetyScoreSection = styled.div`
  border-top: 1px solid var(--x-purple-400, #6a6280);
  padding-top: 16px;
`;

const SafetyScoreValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SafetyScoreNumber = styled.span<{ $color: string }>`
  color: ${(props: { $color: string }) => props.$color};
  font-size: 28px;
  font-family: Inter;
  font-weight: 700;
  line-height: 36px;
`;

const ActivityTitle = styled.div`
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  font-family: Inter;
  font-weight: 400;
  line-height: 21px;
  margin-bottom: 8px;
`;

const DetailValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  color: var(--x-gray-400, #cfd0d3);
  cursor: pointer;
  padding: 2px;
  display: inline-flex;
  align-items: center;

  &:hover {
    color: var(--x-gray-200, #f6f6f7);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: var(--x-gray-200, #f6f6f7);
  margin-bottom: 6px;
`;

const RequiredMark = styled.span`
  color: var(--x-red, #ff595a);
`;

const FormInput = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border-radius: 4px;
  border: 1px solid var(--x-purple-500, #494358);
  background: var(--x-purple-600, #3a3646);
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  box-sizing: border-box;

  &::placeholder {
    color: var(--x-gray-400, #cfd0d3);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border-radius: 4px;
  border: 1px solid var(--x-purple-500, #494358);
  background: var(--x-purple-600, #3a3646);
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  box-sizing: border-box;
  appearance: auto;
`;

const FormTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid var(--x-purple-500, #494358);
  background: var(--x-purple-600, #3a3646);
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  resize: vertical;
  box-sizing: border-box;

  &::placeholder {
    color: var(--x-gray-400, #cfd0d3);
  }
`;

const BypassSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BypassTitle = styled.h2`
  margin: 0;
  color: var(--x-gray-200, #f6f6f7);
  font-size: 20px;
  font-weight: 400;
  line-height: 1.4;
`;

const BypassDescription = styled.p`
  margin: 0;
  color: var(--x-gray-400, #cfd0d3);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
`;

const BypassPanel = styled.div`
  background: var(--x-purple-700, #2c2835);
  border-radius: 8px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BypassTabContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px;
  border-radius: 8px;
  background: var(--x-purple-600, #3a3646);
  width: fit-content;
`;

const BypassTabButton = styled.button<{ active: boolean }>`
  border: 0;
  border-radius: 4px;
  background: ${(props: { active: boolean }) => (props.active ? "var(--x-theme-secondary, #73698a)" : "transparent")};
  color: ${(props: { active: boolean }) => (props.active ? "var(--x-gray-200, #f6f6f7)" : "var(--x-gray-400, #cfd0d3)")};
  font-size: 14px;
  line-height: 1;
  padding: 8px 9px;
  cursor: pointer;

  &:hover {
    color: var(--x-gray-200, #f6f6f7);
  }
`;

const BypassTabDescription = styled.p`
  margin: 0;
  color: var(--x-gray-400, #cfd0d3);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
`;

const BypassInputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  flex-wrap: wrap;
`;

const BypassField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 298px;
  max-width: 100%;
`;

const BypassLabel = styled.label`
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
`;

const BypassInput = styled.input`
  width: 100%;
  height: 32px;
  border-radius: 4px;
  border: 2px solid var(--x-theme-secondary, #73698a);
  background: transparent;
  color: var(--x-gray-200, #f6f6f7);
  padding: 0 12px;
  font-size: 14px;
  line-height: 1;
  box-sizing: border-box;
  outline: none;

  &::placeholder {
    color: var(--x-gray-500, #a9a9ac);
  }
`;

const BypassAddButton = styled.button`
  height: 32px;
  border-radius: 4px;
  border: 2px solid var(--x-red, #ff595a);
  background: var(--x-red, #ff595a);
  color: var(--x-gray-200, #f6f6f7);
  padding: 0 16px;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BypassLimitText = styled.div`
  color: var(--x-gray-400, #cfd0d3);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
`;

const BypassList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  overflow: hidden;
  border-radius: 4px;
`;

const BypassRuleRow = styled.div`
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--x-purple-500, #3b3649);
  padding-left: 9px;
`;

const BypassRuleValue = styled.div`
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BypassDeleteButton = styled.button`
  width: 40px;
  height: 40px;
  border: 0;
  background: transparent;
  color: var(--x-gray-400, #cfd0d3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.7;

  &:hover {
    color: var(--x-red, #ff595a);
    opacity: 1;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const BypassEmptyState = styled.div`
  min-height: 40px;
  display: flex;
  align-items: center;
  padding: 0 9px;
  background: var(--x-purple-500, #3b3649);
  color: var(--x-gray-500, #a9a9ac);
  font-size: 14px;
  line-height: 1.5;
`;

const MAX_BYPASS_RULES = 10;

type MainTab = "policy" | "bypass" | "threats";
type BypassRuleTab = "keywords" | "regexes";

type TFunction = (key: string, options?: Record<string, unknown>) => string;

const getKeywordBypassCopy = (t: TFunction) => ({
  description: t("detail.bypass.keywordDescription"),
  placeholder: t("detail.bypass.keywordPlaceholder"),
  limitLabel: t("detail.bypass.keywordLimit"),
  emptyState: t("detail.bypass.keywordEmpty"),
});

const getRegexBypassCopy = (t: TFunction) => ({
  description: t("detail.bypass.regexDescription"),
  placeholder: t("detail.bypass.regexPlaceholder"),
  limitLabel: t("detail.bypass.regexLimit"),
  emptyState: t("detail.bypass.regexEmpty"),
});

const ApplicationDetail = (): JSX.Element => {
  const { serviceTokenId } = useParams<{ serviceTokenId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useAuth();
  const { t } = useTranslation("applications");
  const policiesData = useSelector(
    (state: RootState) => state.policies?.list?.data,
  ) ?? [];
  const serviceTokensData: ServiceTokenType[] = useSelector(
    (state: RootState) => state.infra.serviceTokens.results,
  ) ?? [];
  const statisticsCache: Record<string, StatisticsResponse> = useSelector(
    (state: RootState) => state.infra.statistics.cache,
  ) ?? {};
  const statisticsLoading: boolean = useSelector((state: RootState) => state.infra.statistics.loading) ?? false;
  const cacheKey = `${serviceTokenId || "global"}_7d`;
  const statistics = statisticsCache[cacheKey] ?? null;
  const dashboardDataRaw = useSelector(
    (state: RootState) => state.dashboard.dashboardData.results,
  );
  const dashboardData: DashboardItem[] = Array.isArray(dashboardDataRaw) ? dashboardDataRaw : [];
  const licenseData = useSelector(
    (state: RootState) => state.dashboard.license.results,
  );

  const [tokenDetail, setTokenDetail] = useState<ServiceTokenType | null>(null);

  const currentToken = useMemo(
    () => tokenDetail ?? serviceTokensData.find((t) => t.service_token_id === serviceTokenId),
    [tokenDetail, serviceTokensData, serviceTokenId],
  );

  const [activeTab, setActiveTab] = useState<MainTab>("policy");
  const [activeBypassTab, setActiveBypassTab] = useState<BypassRuleTab>("keywords");
  const [isConfigurePoliciesOpen, setIsConfigurePoliciesOpen] = useState(false);
  const [appliedPolicyIds, setAppliedPolicyIds] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState("");
  const [regexInput, setRegexInput] = useState("");
  const [isBypassSaving, setIsBypassSaving] = useState(false);

  // Manage Application modal
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [manageForm, setManageForm] = useState({
    name: "",
    group: "",
    description: "",
    status: true,
  });
  const [manageFormInitial, setManageFormInitial] = useState({
    name: "",
    group: "",
    description: "",
    status: true,
  });

  // Delete Application modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Regenerate Service Token modal
  const [isRegenModalOpen, setIsRegenModalOpen] = useState(false);
  const [regenStep, setRegenStep] = useState<"confirm" | "reveal">("confirm");
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [hasCopiedNewToken, setHasCopiedNewToken] = useState(false);

  const groupOptions = useMemo(() => {
    const divisions = serviceTokensData.map((t) => t.division).filter(Boolean);
    return [...new Set(divisions)];
  }, [serviceTokensData]);

  const openManageModal = () => {
    const initial = {
      name: currentToken?.service_token_name ?? "",
      group: currentToken?.division ?? "",
      description: currentToken?.description ?? "",
      status: currentToken?.service_token_status === "enable",
    };
    setManageForm(initial);
    setManageFormInitial(initial);
    setIsManageModalOpen(true);
  };

  const handleSaveManage = async () => {
    if (!serviceTokenId || !auth.user?.access_token) return;
    const result = await patchServiceToken({
      serviceTokenId,
      token: auth.user.access_token,
      data: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        service_token_name: manageForm.name,
        division: manageForm.group,
        description: manageForm.description,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        service_token_status: manageForm.status ? "enable" : "disable",
      },
    });
    if (result.success) {
      // Update infra store cache directly with updated token data
      const updatedTokens = serviceTokensData.map((t) => (
        t.service_token_id === serviceTokenId
          ? { ...t, ...result.data }
          : t
      ));
      dispatch({ type: FETCHED_SERVICE_TOKENS, data: updatedTokens });
      if (result.data) {
        setTokenDetail((prev) => (prev ? { ...prev, ...result.data } : null));
      }
      setIsManageModalOpen(false);
      toast.success(t("toast.appUpdated"));
    }
  };

  const handleRegenerateToken = async () => {
    if (!serviceTokenId || !auth.user?.access_token) return;
    setIsRegenerating(true);
    const result = await regenerateServiceToken({
      serviceTokenId,
      token: auth.user.access_token,
    });
    setIsRegenerating(false);
    if (result.success && result.data?.token) {
      setNewToken(result.data.token);
      setRegenStep("reveal");
    }
  };

  const handleCloseRegenModal = () => {
    setIsRegenModalOpen(false);
    setRegenStep("confirm");
    setNewToken(null);
    setHasCopiedNewToken(false);
    setIsRegenerating(false);
  };

  const handleDeleteApplication = async () => {
    if (!serviceTokenId || !auth.user?.access_token) return;
    const result = await deleteServiceToken({
      serviceTokenId,
      token: auth.user.access_token,
    });
    if (result.success) {
      setIsDeleteModalOpen(false);
      navigate("/applications");
    }
  };

  useEffect(() => {
    if (auth.isAuthenticated && auth.user?.access_token) {
      dispatch(getPolicies({ accessToken: auth.user.access_token, period: 7 }));
      dispatch(fetchServiceTokens(auth.user.access_token));
      dispatch(getDashboardData({ accessToken: auth.user.access_token, period: 7 }));
      dispatch(getLicense(auth.user.access_token));
      if (serviceTokenId) {
        getServiceToken({
          serviceTokenId,
          token: auth.user.access_token,
        }).then((result) => {
          if (result.success && result.data) {
            setTokenDetail(result.data);
          }
        });
        dispatch(fetchStatistics({
          token: auth.user.access_token,
          serviceTokenId,
          range: "7d",
        }));
        dispatch(getPolicyNameMapping(auth.user.access_token));
      }
    }
  }, [auth.isAuthenticated, auth.user?.access_token, dispatch, serviceTokenId]);

  // Sync appliedPolicyIds from token's policy_id_list
  useEffect(() => {
    if (currentToken?.policy_id_list) {
      setAppliedPolicyIds(currentToken.policy_id_list);
    }
  }, [currentToken]);

  type DetailPolicyOption = PolicyOption & {
    violations: number;
  };

  const allPolicies: DetailPolicyOption[] = useMemo(
    () => policiesData.map((p) => ({
      id: p.policy_id,
      policyName: p.policy_name,
      name: POLICY_DISPLAY_NAMES[p.policy_name] ?? p.policy_name,
      description: p.description ?? "",
      riskLevel: p.risk_level ?? 2,
      category: (p.policy_id.startsWith("default_") ? "default" : "custom") as "default" | "custom",
      updatedAt: formatTimestamp(p.updated_at, licenseData?.license_info?.timezone) || "—",
      violations: p.summary?.request_unsafe_count ?? 0,
    })),
    [policiesData, licenseData],
  );

  const { language } = useLanguage();
  const getTranslated = (policy: DetailPolicyOption) => getPolicyLocale(language, policy.policyName, {
    name: policy.name,
    description: policy.description,
  });

  const reqStats = statistics?.request_statistics;
  const latencyStats = statistics?.request_latency_metrics;
  const dashboardItem = dashboardData.find(
    (item) => item.service_token_name === currentToken?.service_token_name,
  );
  const safetyScore = dashboardItem?.summary?.security_score ?? 0;
  const scoreInfo = getScoreColor(safetyScore);
  const threatTraceCount = reqStats?.request_unsafe_count ?? 0;
  const maxRiskLevel = Number(
    ["4", "3", "2", "1"].find(
      (risk) => (dashboardItem?.summary?.unsafe_request_counts_by_max_risk?.[risk] || 0) > 0,
    ) || 0,
  );

  const appliedPolicies = appliedPolicyIds
    .map((id) => allPolicies.find((p) => p.id === id))
    .filter((p): p is DetailPolicyOption => p !== undefined)
    .sort(sortByRiskLevelDesc);
  const allowPatternKeywords = currentToken?.allow_pattern_keywords ?? [];
  const allowPatternRegexes = currentToken?.allow_pattern_regexes ?? [];
  const bypassRulesCount = allowPatternKeywords.length + allowPatternRegexes.length;
  const isKeywordTab = activeBypassTab === "keywords";
  const bypassRules = isKeywordTab ? allowPatternKeywords : allowPatternRegexes;
  const bypassInputValue = isKeywordTab ? keywordInput : regexInput;
  const bypassTabCopy = isKeywordTab ? getKeywordBypassCopy(t) : getRegexBypassCopy(t);

  const syncTokenCache = (patch: Partial<ServiceTokenType>) => {
    if (!serviceTokenId) return;

    const updatedTokens = serviceTokensData.map((token) => (
      token.service_token_id === serviceTokenId
        ? { ...token, ...patch }
        : token
    ));
    dispatch({ type: FETCHED_SERVICE_TOKENS, data: updatedTokens });
    setTokenDetail((prev) => {
      if (prev) return { ...prev, ...patch };
      return currentToken ? { ...currentToken, ...patch } : prev;
    });
  };

  const updateBypassRules = async (patch: Partial<ServiceTokenType>) => {
    if (!serviceTokenId || !auth.user?.access_token) return false;

    setIsBypassSaving(true);
    const result = await patchServiceToken({
      serviceTokenId,
      token: auth.user.access_token,
      data: patch,
    });
    setIsBypassSaving(false);

    if (!result.success) {
      toast.error(result.msg || t("toast.bypassUpdateFailed"));
      return false;
    }

    syncTokenCache(result.data ? { ...patch, ...result.data } : patch);
    return true;
  };

  const handleAddBypassRule = async () => {
    const nextValue = bypassInputValue.trim();
    if (!nextValue) return;

    if (bypassRules.includes(nextValue)) {
      toast.error(t("toast.bypassExists"));
      return;
    }

    if (bypassRules.length >= MAX_BYPASS_RULES) {
      toast.error(t("toast.bypassLimit", { max: MAX_BYPASS_RULES }));
      return;
    }

    if (!isKeywordTab) {
      try {
        // Validate regex locally before sending it to the API.
        // eslint-disable-next-line no-new
        new RegExp(nextValue);
      } catch (error) {
        toast.error(t("toast.invalidRegex"));
        return;
      }
    }

    const success = await updateBypassRules(
      isKeywordTab
        ? {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          allow_pattern_keywords: [...allowPatternKeywords, nextValue],
        }
        : {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          allow_pattern_regexes: [...allowPatternRegexes, nextValue],
        },
    );

    if (success) {
      if (isKeywordTab) {
        setKeywordInput("");
      } else {
        setRegexInput("");
      }
      toast.success(t("toast.bypassUpdated"));
    }
  };

  const handleDeleteBypassRule = async (value: string) => {
    const success = await updateBypassRules(
      isKeywordTab
        ? {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          allow_pattern_keywords: allowPatternKeywords.filter((rule) => rule !== value),
        }
        : {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          allow_pattern_regexes: allowPatternRegexes.filter((rule) => rule !== value),
        },
    );

    if (success) {
      toast.success(t("toast.bypassUpdated"));
    }
  };

  const handleConfirmPolicies = async (selectedIds: string[]) => {
    if (!serviceTokenId || !auth.user?.access_token) return;
    const result = await patchServiceToken({
      serviceTokenId,
      token: auth.user.access_token,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      data: { policy_id_list: selectedIds },
    });
    if (result.success) {
      setAppliedPolicyIds([...selectedIds]);
      // Update infra store cache so navigating away and back shows correct data
      const updatedTokens = serviceTokensData.map((t) => (
        t.service_token_id === serviceTokenId
          // eslint-disable-next-line @typescript-eslint/naming-convention
          ? { ...t, policy_id_list: selectedIds }
          : t
      ));
      dispatch({ type: FETCHED_SERVICE_TOKENS, data: updatedTokens });
    }
  };

  if (!currentToken) {
    return (
      <PanelProvider style={{ overflow: "hidden" }}>
        <div
          style={{
            padding: "12px",
            height: "100%",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <DetailPanel>
            <Breadcrumb>
              <Breadcrumb.Item onClick={() => navigate("/applications")}>
                Applications
              </Breadcrumb.Item>
              <Breadcrumb.Item active />
            </Breadcrumb>
            <div style={{
              display: "flex", justifyContent: "center", alignItems: "center", flex: 1,
            }}
            >
              <Spinner animation="border" variant="primary" />
            </div>
          </DetailPanel>
        </div>
      </PanelProvider>
    );
  }

  return (
    <PanelProvider style={{ overflow: "hidden" }}>
      <div
        style={{
          padding: "12px",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <DetailPanel>
          <Breadcrumb>
            <Breadcrumb.Item onClick={() => navigate("/applications")}>
              {t("detail.breadcrumb")}
            </Breadcrumb.Item>
            <Breadcrumb.Item active />
          </Breadcrumb>
          {/* Application Info */}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <AppTitle style={{ marginBottom: 0 }}>
                {currentToken?.service_token_name ?? ""}
              </AppTitle>
              <StatusTag enabled={currentToken?.service_token_status === "enable"} />
            </div>
            <DropdownButton title={t("detail.manage")} variant="secondary" size="sm" align="end">
              <Dropdown.Item onClick={openManageModal}>{t("detail.manageApp")}</Dropdown.Item>
              <Dropdown.Item onClick={() => setIsRegenModalOpen(true)}>{t("detail.regenToken")}</Dropdown.Item>
              <Dropdown.Item onClick={() => setIsDeleteModalOpen(true)}>{t("detail.deleteApp")}</Dropdown.Item>
            </DropdownButton>
          </div>

          <ContentGrid>
            <LeftPanel>
              <ReactScrollbarsCustom>
                <LeftPanelInner>
                  {statisticsLoading && !statistics ? (
                    <div style={{
                      display: "flex", justifyContent: "center", alignItems: "center", flex: 1, minHeight: 200,
                    }}
                    >
                      <Spinner animation="border" variant="primary" />
                    </div>
                  ) : (
                    <>
                      {/* Safety Score */}
                      <SafetyScoreSection>
                        <DetailLabel>{t("detail.stats.safetyScore")}</DetailLabel>
                        <SafetyScoreValueRow>
                          <SafetyScoreNumber $color={scoreInfo.color}>{safetyScore}</SafetyScoreNumber>
                        </SafetyScoreValueRow>
                      </SafetyScoreSection>

                      {/* Last 7 Days Activity */}
                      <div>
                        <ActivityTitle>{t("detail.stats.last7DaysActivity")}</ActivityTitle>
                        <StatsDetailCard style={{ background: "transparent", padding: 0 }}>
                          <StatsDetailRow>
                            <StatsDetailLabel>{t("detail.stats.maxViolationLevel")}</StatsDetailLabel>
                            <div>{maxRiskLevel > 0 ? <RiskLevelTag level={maxRiskLevel} /> : "—"}</div>
                          </StatsDetailRow>
                          <StatsDetailDivider />
                          <StatsDetailRow>
                            <StatsDetailLabel>{t("detail.stats.totalRequests")}</StatsDetailLabel>
                            <StatsDetailValue>{reqStats?.request_count ?? 0}</StatsDetailValue>
                          </StatsDetailRow>
                          <StatsDetailDivider />
                          <StatsDetailRow>
                            <StatsDetailLabel>{t("detail.stats.policyViolations")}</StatsDetailLabel>
                            <StatsDetailValue>{reqStats?.request_unsafe_count ?? 0}</StatsDetailValue>
                          </StatsDetailRow>
                          <StatsDetailDivider />
                          <StatsDetailRow>
                            <StatsDetailLabel>{t("detail.stats.sessions")}</StatsDetailLabel>
                            <StatsDetailValue>{reqStats?.total_session_count ?? 0}</StatsDetailValue>
                          </StatsDetailRow>
                          <StatsDetailDivider />
                          <StatsDetailRow>
                            <StatsDetailLabel>{t("detail.stats.apiErrors")}</StatsDetailLabel>
                            <StatsDetailValue>{reqStats?.request_error_count ?? 0}</StatsDetailValue>
                          </StatsDetailRow>
                          <StatsDetailDivider />
                          <StatsDetailRow>
                            <StatsDetailLabel>{t("detail.stats.latencyP95")}</StatsDetailLabel>
                            <StatsDetailValue monospace>{`${latencyStats?.p95_response_time_ms ?? 0}ms`}</StatsDetailValue>
                          </StatsDetailRow>
                          <StatsDetailDivider />
                          <StatsDetailRow>
                            <StatsDetailLabel>{t("detail.stats.latencyMax")}</StatsDetailLabel>
                            <StatsDetailValue monospace>{`${latencyStats?.max_response_time_ms ?? 0}ms`}</StatsDetailValue>
                          </StatsDetailRow>
                        </StatsDetailCard>
                      </div>
                    </>
                  )}

                  {/* Service Details */}
                  <ServiceDetailCard>
                    <TokenDetailItem>
                      <DetailLabel>{t("detail.info.tokenId")}</DetailLabel>
                      <DetailValueRow>
                        <DetailValue monospace>
                          {currentToken?.service_token_id ?? serviceTokenId}
                        </DetailValue>
                        <CopyButton
                          type="button"
                          title={t("detail.copyId")}
                          onClick={() => {
                            const id = currentToken?.service_token_id ?? serviceTokenId ?? "";
                            navigator.clipboard.writeText(id);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                        </CopyButton>
                      </DetailValueRow>
                    </TokenDetailItem>
                    <TokenDetailItem>
                      <DetailLabel>{t("detail.info.group")}</DetailLabel>
                      <DetailValue>{currentToken?.division ?? ""}</DetailValue>
                    </TokenDetailItem>
                    <TokenDetailItem>
                      <DetailLabel>{t("detail.info.description")}</DetailLabel>
                      <AppDescription>
                        {currentToken?.description || "—"}
                      </AppDescription>
                    </TokenDetailItem>
                    <TokenDetailItem>
                      <DetailLabel>{t("detail.info.createdTime")}</DetailLabel>
                      <DetailValue monospace>{formatTimestamp(currentToken?.created_at, licenseData?.license_info?.timezone)}</DetailValue>
                    </TokenDetailItem>
                    <TokenDetailItem>
                      <DetailLabel>{t("detail.info.expiredTime")}</DetailLabel>
                      <DetailValue monospace>
                        {formatTimestamp(currentToken?.expires_at, licenseData?.license_info?.timezone)}
                      </DetailValue>
                    </TokenDetailItem>
                    <TokenDetailItem>
                      <DetailLabel>{t("detail.info.usageMode")}</DetailLabel>
                      <DetailValue>{licenseData?.license_info?.mode ?? "—"}</DetailValue>
                    </TokenDetailItem>
                  </ServiceDetailCard>
                </LeftPanelInner>
              </ReactScrollbarsCustom>
            </LeftPanel>

            <RightPanel>
              <ReactScrollbarsCustom>
                <RightPanelInner>
                  <Card>
                    <TabContainer>
                      <Tab
                        active={activeTab === "policy"}
                        onClick={() => setActiveTab("policy")}
                      >
                        <TabLabel>{t("detail.tabs.policy")}</TabLabel>
                      </Tab>
                      <Tab
                        active={activeTab === "bypass"}
                        onClick={() => setActiveTab("bypass")}
                      >
                        <TabLabel>
                          {t("detail.tabs.bypass")}
                          <TabBadge>{bypassRulesCount}</TabBadge>
                        </TabLabel>
                      </Tab>
                      <Tab
                        active={activeTab === "threats"}
                        onClick={() => setActiveTab("threats")}
                      >
                        <TabLabel>
                          {t("detail.tabs.threats")}
                          <TabBadge>{threatTraceCount}</TabBadge>
                        </TabLabel>
                      </Tab>
                    </TabContainer>

                    {activeTab === "policy" && (
                      <>
                        <PoliciesSummaryRow>
                          <PoliciesSummaryText style={{ fontWeight: 700, fontSize: 16 }}>
                            {t("detail.policyTab.title")}
                          </PoliciesSummaryText>
                        </PoliciesSummaryRow>
                        <PoliciesSummaryRow style={{ gap: 18, justifyContent: "flex-start" }}>
                          <Button variant="primary" onClick={() => setIsConfigurePoliciesOpen(true)}>
                            <Edit size={14} color="var(--x-gray-200)" />
                            {` ${t("detail.policyTab.configureButton")}`}
                          </Button>
                          <SectionTitle style={{ margin: 0, flex: 1 }}>
                            {`${t("detail.policyTab.configureHint")} `}
                            <ViolationPolicyTypeTag index={3}>*</ViolationPolicyTypeTag>
                            {` ${t("detail.policyTab.configureHintTail")}`}
                          </SectionTitle>
                        </PoliciesSummaryRow>

                        {appliedPolicies.map((policy) => {
                          const translated = getTranslated(policy);
                          return (
                            <PolicyCard key={policy.id}>
                              <div
                                style={{
                                  display: "flex", alignItems: "center", gap: 8, marginBottom: 4,
                                }}
                              >
                                <RiskLevelTag level={policy.riskLevel} />
                                <PolicyCardName>{translated.name}</PolicyCardName>
                              </div>
                              <PolicyCardDescription>{translated.description}</PolicyCardDescription>
                              <PolicyCardFooter>
                                <PolicyFooterLabel>{t("detail.policyTab.policyType")}</PolicyFooterLabel>
                                <PolicyCategoryTag category={policy.category} />
                                <PolicyFooterLabel style={{ marginLeft: 8 }}>{t("detail.policyTab.last7dViolations")}</PolicyFooterLabel>
                                <ViolationsCount>{policy.violations}</ViolationsCount>
                              </PolicyCardFooter>
                            </PolicyCard>
                          );
                        })}
                      </>
                    )}

                    {activeTab === "bypass" && (
                      <BypassSection>
                        <BypassTitle>{t("detail.bypass.title")}</BypassTitle>
                        <BypassDescription>
                          {t("detail.bypass.description")}
                        </BypassDescription>
                        <BypassPanel>
                          <BypassTabContainer>
                            <BypassTabButton
                              type="button"
                              active={isKeywordTab}
                              onClick={() => setActiveBypassTab("keywords")}
                            >
                              {t("detail.bypass.tabKeyword")}
                            </BypassTabButton>
                            <BypassTabButton
                              type="button"
                              active={!isKeywordTab}
                              onClick={() => setActiveBypassTab("regexes")}
                            >
                              {t("detail.bypass.tabRegex")}
                            </BypassTabButton>
                          </BypassTabContainer>

                          <BypassTabDescription>{bypassTabCopy.description}</BypassTabDescription>

                          <BypassInputRow>
                            <BypassField>
                              <BypassLabel htmlFor={`bypass-rule-input-${activeBypassTab}`}>
                                {t("detail.bypass.addLabel")}
                                <RequiredMark>*</RequiredMark>
                              </BypassLabel>
                              <BypassInput
                                id={`bypass-rule-input-${activeBypassTab}`}
                                placeholder={bypassTabCopy.placeholder}
                                value={bypassInputValue}
                                maxLength={255}
                                disabled={isBypassSaving}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                  if (isKeywordTab) {
                                    setKeywordInput(event.target.value);
                                  } else {
                                    setRegexInput(event.target.value);
                                  }
                                }}
                                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    handleAddBypassRule();
                                  }
                                }}
                              />
                            </BypassField>
                            <BypassAddButton
                              type="button"
                              disabled={isBypassSaving || !bypassInputValue.trim() || bypassRules.length >= MAX_BYPASS_RULES}
                              onClick={handleAddBypassRule}
                            >
                              {t("detail.bypass.addButton")}
                            </BypassAddButton>
                          </BypassInputRow>

                          <BypassLimitText>
                            {bypassTabCopy.limitLabel}
                            {" "}
                            {`${bypassRules.length}/${MAX_BYPASS_RULES}`}
                          </BypassLimitText>

                          <BypassList>
                            {bypassRules.length > 0 ? bypassRules.map((rule) => (
                              <BypassRuleRow key={rule}>
                                <BypassRuleValue title={rule}>{rule}</BypassRuleValue>
                                <BypassDeleteButton
                                  type="button"
                                  aria-label={t("detail.bypass.deleteAria", { rule })}
                                  disabled={isBypassSaving}
                                  onClick={() => handleDeleteBypassRule(rule)}
                                >
                                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path d="M1.75 3.5H12.25" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                    <path d="M5.25 1.75H8.75" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                    <path d="M11.0833 3.5L10.6756 10.2986C10.6171 11.2739 9.80915 12.0312 8.83207 12.0312H5.16793C4.19085 12.0312 3.38291 11.2739 3.32439 10.2986L2.91666 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                    <path d="M5.54175 6.125V9.04167" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                    <path d="M8.45825 6.125V9.04167" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                  </svg>
                                </BypassDeleteButton>
                              </BypassRuleRow>
                            )) : (
                              <BypassEmptyState>{bypassTabCopy.emptyState}</BypassEmptyState>
                            )}
                          </BypassList>
                        </BypassPanel>
                      </BypassSection>
                    )}

                    {activeTab === "threats" && (
                      <ThreatTraceSection hideApplicationColumn serviceTokenId={serviceTokenId} />
                    )}
                  </Card>
                </RightPanelInner>
              </ReactScrollbarsCustom>
            </RightPanel>
          </ContentGrid>
        </DetailPanel>

        <PolicySelectorModal
          show={isConfigurePoliciesOpen}
          onClose={() => setIsConfigurePoliciesOpen(false)}
          policies={allPolicies}
          selectedIds={appliedPolicyIds}
          onConfirm={handleConfirmPolicies}
        />

        {/* Manage Application Modal */}
        <Modal
          title={t("detail.manageModal.title")}
          show={isManageModalOpen}
          onClose={() => setIsManageModalOpen(false)}
          showCloseBtn
          restoreFocus={false}
        >
          <Modal.Body>
            <div
              style={{
                fontWeight: 700, fontSize: 18, color: "var(--x-gray-200)", marginBottom: 16,
              }}
            >
              {t("detail.manageModal.subtitle")}
            </div>
            <FormGroup>
              <FormLabel>
                {t("detail.manageModal.nameLabel")}
                <RequiredMark>*</RequiredMark>
              </FormLabel>
              <FormInput
                maxLength={50}
                value={manageForm.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setManageForm((prev) => ({ ...prev, name: e.target.value }));
                }}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>
                {t("detail.manageModal.groupLabel")}
              </FormLabel>
              <FormSelect
                value={manageForm.group}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setManageForm((prev) => ({ ...prev, group: e.target.value }));
                }}
              >
                <option value="">{t("detail.manageModal.groupPlaceholder")}</option>
                {groupOptions.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </FormSelect>
            </FormGroup>
            <FormGroup>
              <FormLabel>{t("detail.manageModal.descriptionLabel")}</FormLabel>
              <FormTextarea
                placeholder={t("detail.manageModal.descriptionPlaceholder")}
                value={manageForm.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  setManageForm((prev) => ({ ...prev, description: e.target.value }));
                }}
              />
            </FormGroup>
            <div style={{ borderTop: "1px solid var(--x-purple-400)", margin: "16px 0" }} />
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Toggle
                name="manage-app-status"
                checked={manageForm.status}
                type="switch"
                onChange={() => {
                  setManageForm((prev) => ({ ...prev, status: !prev.status }));
                }}
              />
              <span style={{ color: "var(--x-gray-200)", fontSize: 14 }}>{t("detail.manageModal.enabled")}</span>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="secondary" onClick={() => setIsManageModalOpen(false)}>
                {t("detail.manageModal.cancel")}
              </Button>
              <Button variant="primary" onClick={handleSaveManage} disabled={!manageForm.name || (manageForm.name === manageFormInitial.name && manageForm.group === manageFormInitial.group && manageForm.description === manageFormInitial.description && manageForm.status === manageFormInitial.status)}>
                {t("detail.manageModal.save")}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>

        {/* Delete Application Modal */}
        <Modal
          title={t("detail.deleteModal.title")}
          show={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          showCloseBtn
          restoreFocus={false}
        >
          <Modal.Body>
            <div
              style={{
                fontWeight: 700, fontSize: 18, color: "var(--x-gray-200)", marginBottom: 16,
              }}
            >
              {t("detail.deleteModal.subtitle")}
            </div>
            <div style={{ fontSize: 14, color: "var(--x-gray-300)", lineHeight: 1.6 }}>
              {t("detail.deleteModal.body")}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
                {t("detail.deleteModal.cancel")}
              </Button>
              <Button variant="primary" onClick={handleDeleteApplication}>
                {t("detail.deleteModal.delete")}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>

        {/* Regenerate Service Token Modal */}
        <Modal
          title={t("detail.regenModal.title")}
          show={isRegenModalOpen}
          onClose={handleCloseRegenModal}
          showCloseBtn={regenStep === "confirm" ? !isRegenerating : hasCopiedNewToken}
          restoreFocus={false}
        >
          <Modal.Body>
            {regenStep === "confirm" ? (
              <>
                <div
                  style={{
                    background: "rgba(242, 201, 76, 0.08)",
                    border: "1px solid rgba(242, 201, 76, 0.35)",
                    borderRadius: 6,
                    padding: "10px 12px",
                    color: "var(--x-yellow, #f2c94c)",
                    fontSize: 13,
                    lineHeight: 1.5,
                    marginBottom: 16,
                  }}
                >
                  {t("detail.regenModal.warning")}
                </div>
                <div style={{ fontSize: 13, marginBottom: 16 }}>
                  <div style={{
                    display: "flex", justifyContent: "space-between", padding: "6px 0",
                  }}
                  >
                    <span style={{ color: "var(--x-gray-400, #8a8597)" }}>
                      {t("detail.regenModal.applicationLabel")}
                    </span>
                    <span style={{ color: "var(--x-gray-200, #f6f6f7)" }}>
                      {currentToken?.service_token_name ?? ""}
                    </span>
                  </div>
                  <div style={{
                    display: "flex", justifyContent: "space-between", padding: "6px 0",
                  }}
                  >
                    <span style={{ color: "var(--x-gray-400, #8a8597)" }}>
                      {t("detail.regenModal.tokenIdLabel")}
                    </span>
                    <span style={{ color: "var(--x-gray-200, #f6f6f7)", fontFamily: "\"Roboto Mono\", monospace" }}>
                      {serviceTokenId}
                    </span>
                  </div>
                </div>
                <div style={{
                  borderTop: "1px solid var(--x-purple-400)", margin: "12px 0",
                }}
                />
                <div style={{ fontSize: 14, color: "var(--x-gray-200, #f6f6f7)" }}>
                  {t("detail.regenModal.confirmQuestion")}
                </div>
              </>
            ) : (
              <>
                <div style={{
                  color: "var(--x-green, #6fcf97)",
                  fontSize: 13,
                  marginBottom: 16,
                }}
                >
                  {t("detail.regenModal.successMessage")}
                </div>
                <FormGroup>
                  <FormLabel>{t("detail.regenModal.newTokenLabel")}</FormLabel>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <FormInput
                      readOnly
                      value={newToken ?? ""}
                      style={{ flex: 1, fontFamily: "\"Roboto Mono\", monospace" }}
                    />
                    <Button
                      variant="secondary"
                      onClick={() => {
                        if (!newToken) return;
                        navigator.clipboard.writeText(newToken);
                        setHasCopiedNewToken(true);
                        toast.success(t("detail.regenModal.tokenCopied"));
                      }}
                    >
                      {t("detail.regenModal.copyBtn")}
                    </Button>
                  </div>
                </FormGroup>
                <div style={{
                  marginTop: 8, fontSize: 13, color: "var(--x-yellow, #f2c94c)",
                }}
                >
                  {t("detail.regenModal.securityWarning")}
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {regenStep === "confirm" ? (
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant="secondary" onClick={handleCloseRegenModal} disabled={isRegenerating}>
                  {t("detail.regenModal.cancel")}
                </Button>
                <Button variant="primary" onClick={handleRegenerateToken} disabled={isRegenerating}>
                  {isRegenerating ? t("detail.regenModal.regenerating") : t("detail.regenModal.regenerate")}
                </Button>
              </div>
            ) : (
              <Button variant="secondary" onClick={handleCloseRegenModal} disabled={!hasCopiedNewToken}>
                {t("detail.regenModal.close")}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </div>
    </PanelProvider>
  );
};

export default ApplicationDetail;
